<template>
  <v-form ref="permanentPlacementForm" v-model="isFormValid" lazy-validation>
    <v-tabs v-model="tab">
      <v-tab :key="PERMANENT_PLACEMENT">
        Contract Details
      </v-tab>
      <v-tab :key="COMMISSIONS">
        Commissions
      </v-tab>
      <v-tab :key="EXPENSES">
        Expenses
      </v-tab>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <v-tab-item :key="PERMANENT_PLACEMENT">
        <v-row class="mt-3">
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              label="Job Title"
              v-model="jobTitle"
              :readonly="readonly"
              :rules="[required('Job Title')]"
              outlined
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <SimpleDatePicker
              label="Placement Date"
              :value="permPlacementDate"
              :isRequired="true"
              :readonly="readonly"
              @selected-date-change="(newValue) => this.permPlacementDate = newValue"
            ></SimpleDatePicker>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <SimpleDatePicker
              label="Invoice Date"
              :value="permPlacementInvoiceDate"
              :isRequired="false"
              :readonly="readonly"
              @selected-date-change="(newValue) => this.permPlacementInvoiceDate = newValue"
            ></SimpleDatePicker>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <SimpleDatePicker
              label="Invoice Due Date"
              :value="permPlacementInvoiceDueDate"
              :isRequired="false"
              :readonly="readonly"
              @selected-date-change="(newValue) => this.permPlacementInvoiceDueDate = newValue"
            ></SimpleDatePicker>
          </v-col>
        </v-row>
        <v-row class="mt-3 mb-5">
          <v-col cols="12" sm="6" md="4">
            <FormattedNumberField
              label="Salary"
              :value="permPlacementSalary"
              :required="true"
              :readonly="readonly"
              prepend-inner-icon="attach_money"
              @valueChange="(newValue) => this.permPlacementSalary = newValue"
            ></FormattedNumberField>
          </v-col>
          <v-col cols="8" sm="4" md="2">
            <FormattedNumberField
              label="Fee"
              :value="permPlacementFee"
              :rules="[required('Fee'), isValidFee]"
              :readonly="readonly"
              :prepend-inner-icon="permPlacementFeeTypeId === FIXED ?'attach_money' : null"
              :validate-on-blur="true"
              @valueChange="feeChange"
            ></FormattedNumberField>
          </v-col>
          <v-col cols="4" sm="2" md="1" class="pl-0 pt-0">
            <v-radio-group
              v-model="permPlacementFeeTypeId"
              :readonly="readonly"
              :rules="[required('Fee Type')]"
              @change="feeTypeIdChange"
            >
              <v-radio
                label="%"
                :value="PERCENTAGE"
              ></v-radio>
              <v-radio
                label="Fixed"
                :value="FIXED"
              ></v-radio>
            </v-radio-group>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <FormattedNumberField
              label="Net Fee"
              :value="netFee"
              :readonly="true"
              prepend-inner-icon="attach_money"
            ></FormattedNumberField>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <FormattedNumberField
              label="Gross Profit"
              :value="grossProfit"
              :readonly="true"
              prepend-inner-icon="attach_money"
              :hint="grossProfitHint"
              :persistent-hint="!!grossProfitHint"
            ></FormattedNumberField>
          </v-col>
        </v-row>
      </v-tab-item>
      <v-tab-item :key="COMMISSIONS">
        <v-row class="my-5">
          <v-col cols="12" class="px-10">
            <CommissionsCollector
              ref="commissionCollector"
              commissionComponent="CommissionInput"
              commissionType="commission"
              :commissions="commissions"
              :readonly="readonly"
              class="mb-2"
              @commissionsChange="handleCommissionsChange"
            ></CommissionsCollector>
          </v-col>
        </v-row>
      </v-tab-item>
      <v-tab-item :key="EXPENSES">
        <v-row class="my-5">
          <v-col cols="12" class="px-10">
            <ExpensesCard
              class="px-2"
              cardTitle="Expenses"
              :expensesList="expenses"
              :readonly="readonly"
              :showActive="false"
              :showExpenseAmountType="false"
              @expensesListChange="(newValue) => this.expenses = newValue"
            ></ExpensesCard>
          </v-col>
        </v-row>
      </v-tab-item>
    </v-tabs-items>
    <ButtonsSubmitForm
      :acceptLabel="nextButtonLabel"
      cancelLabel="back"
      :isFormValid="true"
      @save="nextStep"
      @cancel="goBack"
    >
    </ButtonsSubmitForm>
    <MessageBox
      ref="messageBox"
      message="There are required fields missing"
      color="error"
      closeTextColor="white"
    ></MessageBox>
  </v-form>
</template>

<script>
import { ButtonsSubmitForm } from '@/components/buttons'
import { required } from '@/lib/formValidations'
import { SimpleDatePicker } from '@/components/shared/datePickers'
import { CommissionsCollector } from '@/components/commissions'
import { ExpensesCard } from '@/components/expenses'
import { mapFields } from '@/vueHelpers/vuex'
import { mapActions } from 'vuex'
import { PERCENTAGE, FIXED } from '@/lib/constants/permPlacementFeeTypes'
import { CREATE_FROM_ZOHO } from '@/lib/constants/dealCreationTypes'
import { getTotalCommisions } from '@/lib/commissions'
import { PRE_GP } from '@/lib/constants/expensesTypes'
import { formatCurrency } from '@/lib/display'
import { FormattedNumberField } from '@/components/shared/textFields'
import { MessageBox } from '@/components/shared/messages'

export default {
  props: [
    'readonly'
  ],
  components: {
    ButtonsSubmitForm,
    SimpleDatePicker,
    CommissionsCollector,
    ExpensesCard,
    FormattedNumberField,
    MessageBox
  },
  data () {
    return {
      tab: null,
      isFormValid: null,
      PERCENTAGE,
      FIXED,
      CREATE_FROM_ZOHO,
      PERMANENT_PLACEMENT: 1,
      COMMISSIONS: 2,
      EXPENSES: 3
    }
  },
  computed: {
    ...mapFields('deal', ['id', 'statusId', 'dealCreationTypeId', 'dealAssignmentId']),
    ...mapFields('dealClient', ['existingClientId', 'directPlacementsExpenses']),
    ...mapFields('dealAssignment', [
      'dealId',
      'jobTitle',
      'permPlacementDate',
      'permPlacementSalary',
      'permPlacementFee',
      'permPlacementFeeTypeId',
      'permPlacementInvoiceDate',
      'permPlacementInvoiceDueDate',
      'commissions',
      'expenses',
      'expensesDeduction',
      'expensesDeductionHint'
    ]),
    netFee () {
      return this.permPlacementSalary && this.permPlacementFee && this.permPlacementFeeTypeId
        ? (this.permPlacementFeeTypeId === PERCENTAGE
          ? (this.permPlacementSalary * this.permPlacementFee) / 100 : this.permPlacementFee)
        : null
    },
    nextButtonLabel () {
      return (!this.statusId && this.dealCreationTypeId === CREATE_FROM_ZOHO) || this.readonly ? 'next' : 'save'
    },
    grossProfit () {
      if (this.netFee) {
        return (this.netFee - this.expensesDeduction).toFixed(2)
      } else {
        return null
      }
    },
    grossProfitHint () {
      if (this.netFee) {
        let hint = `Gross Profit = Net Fee (${formatCurrency(this.netFee)})`
        return this.expensesDeduction > 0 ? `${hint} - ${this.expensesDeductionHint}` : hint
      } else {
        return null
      }
    }
  },
  watch: {
    directPlacementsExpenses () {
      // First delete the default expenses
      if (this.expenses && this.expenses.length > 0) {
        // Look for the client's default expenses
        let defaultExpenses = this.expenses.filter(expense => !!expense.clientDefaultExpense)
        for (let defaultExpense of defaultExpenses) {
          let indexToDelete = this.expenses.findIndex(expense => expense.id === defaultExpense.id && !!expense.clientDefaultExpense)
          this.expenses.splice(indexToDelete, 1)
        }
      }
      // Then include the default expenses of the existing client chosen
      if (this.directPlacementsExpenses && this.directPlacementsExpenses.length > 0) {
        for (let index = this.directPlacementsExpenses.length - 1; index >= 0; index--) {
          this.expenses.unshift({ ...this.directPlacementsExpenses[index], clientDefaultExpense: true })
        }
      }
    },
    expenses () {
      // Set expense deduction
      this.expensesDeduction = 0
      this.expensesDeductionHint = ''
      this.expenses.map((expense) => {
        if (expense.expenseTypeId === PRE_GP) {
          this.expensesDeduction += parseFloat(expense.amount)
          this.expensesDeductionHint += `${expense.expenseName} (${formatCurrency(expense.amount)}) - `
        }
      })
      this.expensesDeductionHint = this.expensesDeductionHint.substring(0, this.expensesDeductionHint.length - 3)
    }
  },
  methods: {
    getTotalCommisions,
    ...mapActions('dealAssignment', ['saveDealAssignment']),
    required,
    checkRequiredFields () {
      return this.jobTitle &&
      this.permPlacementDate &&
      this.permPlacementSalary &&
      this.permPlacementFee &&
      this.permPlacementFeeTypeId
    },
    nextStep () {
      if (!this.readonly && !!this.statusId) {
        if (this.$refs.permanentPlacementForm.validate() &&
        this.checkRequiredFields() &&
        (this.$refs.commissionCollector ? this.$refs.commissionCollector.validateCommissionsPercentages() : true)
        ) {
          this.dealId = this.id
          this.saveDealAssignment().then((response) => {
            this.dealAssignmentId = response.data.id
            this.$emit('complete')
          })
        } else {
          this.$refs.messageBox.show()
        }
      } else {
        this.$emit('complete')
      }
    },
    goBack () {
      this.$emit('goBack')
    },
    isValidFee () {
      return this.permPlacementFee && this.permPlacementFeeTypeId
        ? (this.permPlacementFeeTypeId === PERCENTAGE
          ? (parseInt(this.permPlacementFee) <= 100 ? true : 'The fee percent is not valid.') : true) : true
    },
    handleCommissionsChange (value) {
      this.commissions = value
    },
    feeChange (newValue) {
      this.permPlacementFee = newValue
      if (this.permPlacementFee && !this.permPlacementFeeTypeId) {
        if (this.permPlacementFee > 100) {
          this.permPlacementFeeTypeId = FIXED
        } else {
          this.permPlacementFeeTypeId = PERCENTAGE
        }
      }
    },
    feeTypeIdChange () {
      if (this.permPlacementFee && this.permPlacementFee > 100 && this.permPlacementFeeTypeId === PERCENTAGE) {
        this.permPlacementFee = null
      }
      this.$refs.permanentPlacementForm.resetValidation()
    }
  }
}
</script>
